import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
} from "@mui/material";
import Header from "../components/ContentPage/Header";
import PrintIcon from "@mui/icons-material/Print";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BlurLinearIcon from "@mui/icons-material/BlurLinear";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CompareIcon from "@mui/icons-material/Compare";
import DndItem from "../components/TaxComputationConsole/SchedulesList/DndItem";
import CADndItem from "../components/TaxComputationConsole/SchedulesList/CADndItem";
import { downloadAllFromJson } from "../components/ContentPage/utils/generator";
import { isEmptyObject } from "../utils/helpers";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SCHEDULE_MENU, TAX_COMPUTATION_MENU } from "../utils/data/controlData";
import { AuthContext } from "../contexts/AuthContext";
import RolloverDataDialog from "../components/ContentPage/RolloverDataDialog";
import RolloverProgressDialog from "../components/ContentPage/RolloverProgressDialog";
import RolloverConfirmationDialog from "../components/ContentPage/RolloverConfirmationDialog";
import PostRolloverReminderDialog from "../components/ContentPage/PostRolloverReminderDialog";
import useRolloverComputations from "../components/ContentPage/hooks/useRolloverComputations";
import useRolloverExecution from "../components/ContentPage/hooks/useRolloverExecution";
import DownloadMenuOptions from "../components/ContentPage/DownloadMenuOptions";
import ConfirmationDialog from "../components/utils/dialog/confirmationDialog";
import DialogLoading from "../components/App/DialogLoading";
import { useAccessControl } from "../utils/hooks/useAccessControl";
import SchedulePanel from "../components/ContentPage/SchedulePanel";
import BaseDialog from "../components/utils/dialog/BaseDialog";
import PdfGenerationOptionsDialog from "../components/ContentPage/PdfGenerationOptionsDialog";
import {
  deleteAllPdfVersions,
  deletePdfVersion,
  getPdfVersion,
  listPdfVersions,
  savePdfVersion,
  updatePdfVersionName,
} from "../contexts/services/pdfVersion/pdfVersionServices";
import { comparePdfBase64WithBlob } from "../utils/pdf/comparePdf";
import {
  getUserPreference,
  updatePdfPreference,
} from "../contexts/services/userPreferenceServices";
import {
  areFullTaxComputationPdfOptionsEqual,
  normalizeFullTaxComputationPdfOptions,
} from "../components/ContentPage/utils/pdfFooterOptions";

const PDF_VERSION_STORAGE_LIMIT_BYTES = 25 * 1024 * 1024;

const buildDefaultPdfOutputOptions = () =>
  normalizeFullTaxComputationPdfOptions();

const getSavedPdfOutputOptions = (preference) =>
  normalizeFullTaxComputationPdfOptions(preference?.pdf?.fullTaxComputation);

const ContentPage = ({
  states,
  company,
  yearOfAssessment,
  plSchedules,
  caSchedules,
  isFetching,
  handleError,
  handleSuccess,
  onAddSchedule,
  onOpenSchedule,
  reorderSchedule,
  moveSchedule,
  getPlScheduleDoc,
  getCaScheduleDoc,
  fetchStatementDocs,
  fetchCASummaryPdfDetails,
  onAddCaSchedule,
  onOpenCaSchedule,
  reorderCaSchedule,
  moveCaSchedule,
}) => {
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { getTaxComputationList, rollOverTaxComputation } =
    useContext(AuthContext);

  const { canEdit: canEditTaxComputation } =
    useAccessControl(TAX_COMPUTATION_MENU);
  const { canEdit: canEditSchedule } = useAccessControl(SCHEDULE_MENU);

  const [anchorEl, setAnchorEl] = useState(null);
  const [plSearchQuery, setPlSearchQuery] = useState("");
  const [caSearchQuery, setCaSearchQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false);
  const [confirmRolloverDialogOpen, setConfirmRolloverDialogOpen] =
    useState(false);
  const [pendingRolloverData, setPendingRolloverData] = useState(null);
  const [postRolloverDialogOpen, setPostRolloverDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [pdfVersions, setPdfVersions] = useState([]);
  const [pdfVersionMeta, setPdfVersionMeta] = useState({
    maxStorageBytes: PDF_VERSION_STORAGE_LIMIT_BYTES,
    usedStorageBytes: 0,
    remainingStorageBytes: PDF_VERSION_STORAGE_LIMIT_BYTES,
  });
  const [isLoadingPdfVersions, setIsLoadingPdfVersions] = useState(false);
  const [pdfVersionActionId, setPdfVersionActionId] = useState(null);
  const [comparingPdfVersionId, setComparingPdfVersionId] = useState(null);
  const [pdfComparisonResult, setPdfComparisonResult] = useState(null);
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editingVersionName, setEditingVersionName] = useState("");
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [deleteAllVersionsDialogOpen, setDeleteAllVersionsDialogOpen] =
    useState(false);
  const [pdfVersionError, setPdfVersionError] = useState("");
  const [storePromptOpen, setStorePromptOpen] = useState(false);
  const [pendingPdfDownload, setPendingPdfDownload] = useState(null);
  const [pdfOutputOptions, setPdfOutputOptions] = useState(() =>
    buildDefaultPdfOutputOptions(),
  );
  const savedPdfOutputOptionsRef = useRef(buildDefaultPdfOutputOptions());
  const [isLoadingPdfOutputPreference, setIsLoadingPdfOutputPreference] =
    useState(true);
  const [isSavingPdfOutputPreference, setIsSavingPdfOutputPreference] =
    useState(false);
  const [isProcessingPdfOutputContinue, setIsProcessingPdfOutputContinue] =
    useState(false);
  const [pdfGenerationLoadingOpen, setPdfGenerationLoadingOpen] =
    useState(false);
  const [storeVersionDialogOpen, setStoreVersionDialogOpen] = useState(false);
  const [storeVersionFileName, setStoreVersionFileName] = useState("");
  const [storeVersionPdfType, setStoreVersionPdfType] = useState("final");
  const [storeVersionLimitInfo, setStoreVersionLimitInfo] = useState(null);
  const [storeVersionError, setStoreVersionError] = useState("");
  const [isLoadingStoreVersionLimit, setIsLoadingStoreVersionLimit] =
    useState(false);
  const [isStoringPdfVersion, setIsStoringPdfVersion] = useState(false);

  const currentTaxComputationId = company?.tc_id || states?.details?.tc_id;
  const rolloverComputations = useRolloverComputations({
    open: rolloverDialogOpen,
    companyId: states?.company?.c_id,
    getTaxComputationList,
  });
  const rolloverExecution = useRolloverExecution({
    targetId: currentTaxComputationId,
    rollOverTaxComputation,
  });

  useEffect(() => {
    let isMounted = true;

    getUserPreference()
      .then((preference) => {
        if (!isMounted) return;

        const nextOptions = getSavedPdfOutputOptions(preference);
        savedPdfOutputOptionsRef.current = nextOptions;
        setPdfOutputOptions(nextOptions);
      })
      .catch(() => {
        // PDF generation remains available with defaults if preferences fail.
      })
      .finally(() => {
        if (isMounted) setIsLoadingPdfOutputPreference(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pdfComparisonResult?.url) {
        URL.revokeObjectURL(pdfComparisonResult.url);
      }
    };
  }, [pdfComparisonResult?.url]);

  // Filter schedules based on search query
  const filteredPlSchedules = plSchedules
    ? plSchedules.filter(
        (schedule) =>
          schedule.scheduleTitle
            .toLowerCase()
            .includes(plSearchQuery.toLowerCase()) ||
          schedule.scheduleNo.toString().includes(plSearchQuery),
      )
    : [];

  const filteredCaSchedules = caSchedules
    ? caSchedules.filter(
        (schedule) =>
          schedule.scheduleTitle
            .toLowerCase()
            .includes(caSearchQuery.toLowerCase()) ||
          schedule.scheduleNo.toString().includes(caSearchQuery),
      )
    : [];

  // Check for post-rollover flag on component mount
  useEffect(() => {
    if (!currentTaxComputationId) return;
    let timer;
    try {
      const flag = sessionStorage.getItem("rolloverCompleted");
      if (
        flag &&
        (flag === "true" ||
          JSON.parse(flag).tc_id === currentTaxComputationId)
      ) {
        timer = setTimeout(() => {
          try {
            sessionStorage.removeItem("rolloverCompleted");
          } catch (_storageError) {
            // Storage may be unavailable.
          }
          setPostRolloverDialogOpen(true);
        }, 500);
      }
    } catch (_storageError) {
      // The computation is usable even if browser storage is unavailable.
    }
    return () => clearTimeout(timer);
  }, [currentTaxComputationId]);

  const handleRolloverConfirm = (rolloverData) => {
    if (rolloverExecution.busy) return;
    // Validate required data
    if (
      !rolloverData?.sourceComputation?.tc_id ||
      !rolloverData?.rolloverType
    ) {
      handleError(
        "Invalid rollover data. Please select a computation and rollover type.",
      );
      return;
    }

    // Check if full rollover is selected
    if (rolloverData.rolloverType === "full") {
      handleError(
        "Full rollover feature is still under development. Please use partial rollover instead.",
      );
      return;
    }

    // Store rollover data and show confirmation dialog
    setPendingRolloverData(rolloverData);
    setRolloverDialogOpen(false);
    setConfirmRolloverDialogOpen(true);
  };

  const executeRollover = async () => {
    if (!pendingRolloverData || rolloverExecution.busy) return;
    setConfirmRolloverDialogOpen(false);
    await rolloverExecution.execute(pendingRolloverData);
  };

  // Common styles for empty state
  const emptyStateStyles = {
    box: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      minHeight: "200px",
      width: "100%",
      position: "relative",
      backgroundColor: "background.paper",
      borderRadius: 2,
      p: 4,
      border: "1px dashed",
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
    },
    typography: {
      color: "text.secondary",
      fontStyle: "italic",
      mb: 1,
    },
  };

  const showPdfGenerationCompletion = ({
    download = true,
    errorFiles = [],
    fileName,
    stored = false,
  }) => {
    const completedActions = [
      "generated",
      ...(download ? ["downloaded"] : []),
      ...(stored ? ["stored"] : []),
    ];
    const actionLabel =
      completedActions.length === 2
        ? completedActions.join(" and ")
        : completedActions.length > 2
          ? `${completedActions.slice(0, -1).join(", ")}, and ${completedActions.at(-1)}`
          : completedActions[0];
    const storedFileLabel = stored && fileName ? ` as "${fileName}"` : "";

    if (errorFiles.length > 0) {
      const failedListDisplay = errorFiles
        .map((failedFile) => `- ${failedFile}`)
        .join("\n");

      handleError(
        `Your full tax computation PDF was ${actionLabel}${storedFileLabel}, but the following schedules could not be included:\n\n${failedListDisplay}\n\nPlease check and resave these schedules before trying again.`,
      );
      return;
    }

    handleSuccess?.(
      `Your full tax computation PDF was ${actionLabel} successfully${storedFileLabel}.`,
    );
  };

  const executeDownloadAllSchedules = async (
    downloadType,
    forView = false,
    options = {},
  ) => {
    try {
      setIsGenerating(true);
      setAnchorEl(null); // Close the menu immediately when starting

      let newSch = await getPlScheduleDoc();
      let newCaSch = await getCaScheduleDoc();
      let caSummaryPdfDoc = await fetchCASummaryPdfDetails();

      let toc = [];
      let pdfPlDataArray = [];
      let pdfCaDataArray = [];

      let mainComputationDoc = {};
      let detailPlDoc = {};
      // Only include Main Tax Computation and Detailed Income Statement if the switch is enabled
      const statements = await fetchStatementDocs(company.tc_id);

      if (statements) {
        if (statements.mainCompDoc) {
          mainComputationDoc = {
            jsonData: statements.mainCompDoc,
            scheduleName: "Main Tax Computation",
            updatedAt: new Date(),
          };
          toc.push({
            scheduleName: "Main Tax Computation",
            scheduleNo: "Main Comp",
          });
        }
        if (statements.detailsPLDoc) {
          detailPlDoc = {
            jsonData: statements.detailsPLDoc,
            scheduleName: "Detailed Income Statement",
            updatedAt: new Date(),
          };
          toc.push({
            scheduleName: "Detailed Income Statement",
            scheduleNo: "P&L",
          });
        }
      }

      if (newSch && newSch.length > 0) {
        newSch.map((sch) => {
          if (sch.doc && !isEmptyObject(sch.doc)) {
            toc.push({
              scheduleName: sch.scheduleTitle,
              scheduleNo: `Sch ${sch.scheduleNo}`,
            });
            pdfPlDataArray.push({
              jsonData: sch.doc,
              scheduleName: sch.scheduleTitle,
              scheduleType: sch.scheduleType,
              scheduleNo: sch.scheduleNo,
              updatedAt: sch.updatedAt ?? new Date(),
            });
          }
        });
      }

      if (newCaSch && newCaSch.length > 0) {
        newCaSch.map((sch) => {
          if (sch.scheduleType !== "CASummary") {
            if (sch.doc && !isEmptyObject(sch.doc)) {
              toc.push({
                scheduleName: sch.scheduleTitle,
                scheduleNo: `Sch ${sch.scheduleNo}`,
              });
              pdfCaDataArray.push({
                jsonData: sch.doc,
                scheduleName: sch.scheduleTitle,
                scheduleType: sch.scheduleType,
                scheduleNo: sch.scheduleNo,
                updatedAt: sch.updatedAt ?? new Date(),
              });
            }
          } else {
            if (caSummaryPdfDoc && !isEmptyObject(caSummaryPdfDoc)) {
              toc.push({
                scheduleName: sch.scheduleTitle,
                scheduleNo: `Sch ${sch.scheduleNo}`,
              });
              pdfCaDataArray.push({
                jsonData: caSummaryPdfDoc,
                scheduleName: sch.scheduleTitle,
                scheduleType: sch.scheduleType,
                scheduleNo: sch.scheduleNo,
                updatedAt: sch.updatedAt ?? new Date(),
              });
            }
          }
        });
      }

      let errorFiles = [];
      if (toc.length > 0) {
        const filename = `${company.name}_${yearOfAssessment}_Tax Computation`;
        const downloadResult = await downloadAllFromJson(
          company,
          toc,
          mainComputationDoc,
          detailPlDoc,
          pdfPlDataArray,
          pdfCaDataArray,
          filename,
          downloadType,
          forView,
          (fileError, error) => {
            errorFiles.push(fileError);
            console.error(error);
          },
          {
            download: options.download !== false,
            addDraftFooter: options.addDraftFooter ?? forView,
            footer: options.footer,
          },
        );

        if (downloadType === "merged" && !downloadResult?.blob) {
          throw new Error("Unable to generate the combined PDF.");
        }

        if (options.storePdf && downloadResult?.blob) {
          await savePrintAllPdfVersion(downloadResult.blob, {
            fileName: options.fileName,
            pdfType: forView ? "draft" : "final",
          });
        }

        if (!options.suppressCompletionMessage) {
          showPdfGenerationCompletion({
            download: options.download !== false,
            errorFiles,
            fileName: options.fileName,
            stored: options.storePdf === true,
          });
        }

        return {
          ...(downloadResult || {}),
          errorFiles,
        };
      } else {
        if (!options.suppressError) {
          handleError("No available schedule to download!");
        }
        if (options.throwOnError) {
          throw new Error("No available schedule to download!");
        }
      }
    } catch (error) {
      if (!options.suppressError) {
        handleError(error);
      }
      if (options.throwOnError) {
        throw error;
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadAllSchedules = async (downloadType, forView = false) => {
    if (downloadType === "merged") {
      setAnchorEl(null);
      setPendingPdfDownload({ downloadType, forView, download: true });
      setIsLoadingPdfOutputPreference(true);

      try {
        const preference = await getUserPreference();
        const savedOptions = getSavedPdfOutputOptions(preference);
        savedPdfOutputOptionsRef.current = savedOptions;
        setPdfOutputOptions(savedOptions);
      } catch {
        setPdfOutputOptions(
          normalizeFullTaxComputationPdfOptions(
            savedPdfOutputOptionsRef.current,
          ),
        );
      } finally {
        setIsLoadingPdfOutputPreference(false);
      }

      setStorePromptOpen(true);
      return;
    }

    executeDownloadAllSchedules(downloadType, forView);
  };

  const getDefaultPrintAllVersionName = (pdfType) => {
    const baseName = `${company?.name || "Tax Computation"}_${yearOfAssessment}_Tax Computation`;
    return `${pdfType === "draft" ? "Draft" : "Final"} ${baseName}`;
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(
          String(reader.result || "")
            .replace(/^data:application\/pdf(?:;[^,]*)?;base64,/i, "")
            .trim(),
        );
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const formatPdfVersionSize = (size) => {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getPdfStorageLabel = (meta) => {
    const remaining = Number(meta?.remainingStorageBytes || 0);
    const max = Number(meta?.maxStorageBytes || 0);
    return `${formatPdfVersionSize(remaining)} of ${formatPdfVersionSize(max)} available`;
  };

  const savePrintAllPdfVersion = async (blob, { fileName, pdfType }) => {
    if (!currentTaxComputationId || !company?.c_id) {
      throw new Error("Unable to identify current tax computation.");
    }

    if (
      storeVersionLimitInfo?.remainingStorageBytes !== undefined &&
      blob.size > storeVersionLimitInfo.remainingStorageBytes
    ) {
      throw new Error(
        `This PDF is ${formatPdfVersionSize(blob.size)}, but only ${formatPdfVersionSize(
          storeVersionLimitInfo.remainingStorageBytes,
        )} is available.`,
      );
    }

    await savePdfVersion({
      c_id: company.c_id,
      tc_id: currentTaxComputationId,
      fileName,
      pdfType,
      pdfBase64: await blobToBase64(blob),
    });
  };

  const openStoreVersionDialog = async ({
    forView = false,
    download = false,
    footer,
    lockPdfType = false,
  } = {}) => {
    const pdfType = forView ? "draft" : "final";
    setStoreVersionPdfType(pdfType);
    setStoreVersionFileName(getDefaultPrintAllVersionName(pdfType));
    setStoreVersionError("");
    setStoreVersionLimitInfo(null);
    setPendingPdfDownload({
      downloadType: "merged",
      forView,
      download,
      footer:
        footer ??
        normalizeFullTaxComputationPdfOptions(savedPdfOutputOptionsRef.current)
          .footer,
      lockPdfType,
    });
    setStoreVersionDialogOpen(true);

    if (!currentTaxComputationId || !company?.c_id) {
      setStoreVersionError("Unable to identify current tax computation.");
      return;
    }

    setIsLoadingStoreVersionLimit(true);
    try {
      const response = await listPdfVersions(currentTaxComputationId);
      setStoreVersionLimitInfo(response?.data || null);
    } catch (error) {
      setStoreVersionError(
        error?.message || "Unable to check stored PDF versions.",
      );
    } finally {
      setIsLoadingStoreVersionLimit(false);
    }
  };

  const handleStoreVersionTypeChange = (_, nextType) => {
    if (!nextType) return;
    setStoreVersionPdfType(nextType);
    setStoreVersionFileName(getDefaultPrintAllVersionName(nextType));
    setPendingPdfDownload((current) => ({
      ...(current || { downloadType: "merged" }),
      forView: nextType === "draft",
    }));
  };

  const handleClosePdfOutputOptions = () => {
    if (
      isGenerating ||
      isStoringPdfVersion ||
      isSavingPdfOutputPreference ||
      isProcessingPdfOutputContinue
    )
      return;

    setStorePromptOpen(false);
    setPendingPdfDownload(null);
    setPdfOutputOptions(
      normalizeFullTaxComputationPdfOptions(savedPdfOutputOptionsRef.current),
    );
  };

  const handlePdfFooterChange = (footer) => {
    setPdfOutputOptions((current) => ({
      ...current,
      footer,
    }));
  };

  const handlePdfStorePreferenceChange = (storePdf) => {
    setPdfOutputOptions((current) => ({ ...current, storePdf }));
  };

  const savePdfOutputPreferenceIfChanged = async () => {
    const nextOptions = normalizeFullTaxComputationPdfOptions(pdfOutputOptions);

    if (
      areFullTaxComputationPdfOptionsEqual(
        nextOptions,
        savedPdfOutputOptionsRef.current,
      )
    ) {
      return nextOptions;
    }

    setIsSavingPdfOutputPreference(true);
    try {
      const response = await updatePdfPreference({
        value: {
          fullTaxComputation: nextOptions,
        },
      });
      const savedPreference = response?.data || response;
      const savedOptions = getSavedPdfOutputOptions(savedPreference);

      savedPdfOutputOptionsRef.current = savedOptions;
      setPdfOutputOptions(savedOptions);
      return savedOptions;
    } finally {
      setIsSavingPdfOutputPreference(false);
    }
  };

  const handleContinuePdfOutput = async () => {
    const pending = pendingPdfDownload;
    let outputOptions;

    setIsProcessingPdfOutputContinue(true);
    setStorePromptOpen(false);
    setPdfGenerationLoadingOpen(true);
    try {
      outputOptions = await savePdfOutputPreferenceIfChanged();

      if (outputOptions.storePdf) {
        await openStoreVersionDialog({
          forView: pending?.forView || false,
          download: pending?.download !== false,
          footer: outputOptions.footer,
          lockPdfType: true,
        });
        return;
      }

      if (pending) {
        const generationResult = await executeDownloadAllSchedules(
          pending.downloadType,
          pending.forView,
          {
            download: pending.download !== false,
            footer: outputOptions.footer,
            suppressCompletionMessage: true,
            suppressError: true,
            throwOnError: true,
          },
        );

        showPdfGenerationCompletion({
          download: pending.download !== false,
          errorFiles: generationResult?.errorFiles,
        });
      }

      setStorePromptOpen(false);
      setPendingPdfDownload(null);
    } catch (error) {
      handleError(
        error?.message || "Unable to prepare your PDF. Please try again.",
      );
      setStorePromptOpen(true);
    } finally {
      setIsProcessingPdfOutputContinue(false);
      setPdfGenerationLoadingOpen(false);
    }
  };

  const handleStorePrintAllPdfVersion = async () => {
    const fileName = storeVersionFileName.trim();
    if (!fileName) {
      setStoreVersionError("File name is required.");
      return;
    }

    if (storeVersionLimitInfo?.remainingStorageBytes === 0) {
      setStoreVersionError(
        "There is no available PDF storage for this tax computation.",
      );
      return;
    }

    const pending = pendingPdfDownload || {
      downloadType: "merged",
      forView: storeVersionPdfType === "draft",
      download: false,
    };

    setIsStoringPdfVersion(true);
    setStoreVersionError("");
    setStoreVersionDialogOpen(false);
    setPdfGenerationLoadingOpen(true);

    try {
      const generationResult = await executeDownloadAllSchedules(
        "merged",
        storeVersionPdfType === "draft",
        {
          download: pending.download !== false,
          storePdf: true,
          fileName,
          suppressCompletionMessage: true,
          suppressError: true,
          throwOnError: true,
          footer: pending.footer,
        },
      );
      setStoreVersionDialogOpen(false);
      setPendingPdfDownload(null);
      await loadPdfVersions();
      showPdfGenerationCompletion({
        download: pending.download !== false,
        errorFiles: generationResult?.errorFiles,
        fileName,
        stored: true,
      });
    } catch (error) {
      setStoreVersionError(error?.message || "Failed to store PDF version.");
      setStoreVersionDialogOpen(true);
    } finally {
      setIsStoringPdfVersion(false);
      setPdfGenerationLoadingOpen(false);
    }
  };

  const loadPdfVersions = async () => {
    if (!currentTaxComputationId) {
      setPdfVersionError("Unable to identify current tax computation.");
      return;
    }

    setIsLoadingPdfVersions(true);
    setPdfVersionError("");

    try {
      const response = await listPdfVersions(currentTaxComputationId);
      const data = response?.data || {};
      setPdfVersions(data.versions || []);
      setPdfVersionMeta({
        maxStorageBytes:
          data.maxStorageBytes || PDF_VERSION_STORAGE_LIMIT_BYTES,
        usedStorageBytes: data.usedStorageBytes || 0,
        remainingStorageBytes: data.remainingStorageBytes ?? 0,
      });
    } catch (error) {
      setPdfVersionError(
        error?.message || "Failed to load saved PDF versions.",
      );
    } finally {
      setIsLoadingPdfVersions(false);
    }
  };

  const handleOpenVersionDialog = () => {
    setVersionDialogOpen(true);
    setEditingVersionId(null);
    setEditingVersionName("");
    loadPdfVersions();
  };

  const getPdfBlobFromBase64 = (pdfBase64) => {
    const cleanBase64 = String(pdfBase64 || "")
      .replace(/^data:application\/pdf(?:;[^,]*)?;base64,/i, "")
      .trim();
    const byteCharacters = window.atob(cleanBase64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: "application/pdf" });
  };

  const buildPdfVersionFileName = (version) => {
    const cleanName = String(version?.fileName || "PDF Version")
      .replace(/[<>:"/\\|?*]/g, "")
      .trim();
    return cleanName.toLowerCase().endsWith(".pdf")
      ? cleanName
      : `${cleanName}.pdf`;
  };

  const handleDownloadPdfVersion = async (version) => {
    setPdfVersionActionId(version.id);
    setPdfVersionError("");

    try {
      const response = await getPdfVersion(version.id);
      const file = response?.data;
      const blob = getPdfBlobFromBase64(file?.pdfBase64);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = buildPdfVersionFileName(file || version);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfVersionError(error?.message || "Failed to download PDF version.");
    } finally {
      setPdfVersionActionId(null);
    }
  };

  const handleComparePdfVersion = async (version) => {
    setComparingPdfVersionId(version.id);
    setPdfVersionError("");
    setPdfComparisonResult(null);

    try {
      const response = await getPdfVersion(version.id);
      const savedVersion = response?.data || version;
      const currentPdf = await executeDownloadAllSchedules(
        "merged",
        savedVersion.pdfType === "draft",
        {
          download: false,
          throwOnError: true,
          suppressError: true,
          footer: normalizeFullTaxComputationPdfOptions(
            savedPdfOutputOptionsRef.current,
          ).footer,
        },
      );

      if (!currentPdf?.blob) {
        throw new Error("Unable to generate current PDF for comparison.");
      }

      const comparisonUrl = await comparePdfBase64WithBlob(
        savedVersion.pdfBase64,
        currentPdf.blob,
        {
          title: `${savedVersion.pdfType === "draft" ? "Draft" : "Final"} Tax Computation PDF`,
          storedLabel: savedVersion.fileName || version.fileName,
          highlightDifferences: true,
          openMode: "returnUrl",
        },
      );
      setPdfComparisonResult({
        url: comparisonUrl,
        label: savedVersion.fileName || version.fileName || "PDF comparison",
      });
      handleSuccess?.(
        "PDF comparison generated. Click Open Comparison to view it.",
      );
    } catch {
      setPdfComparisonResult(null);
      setPdfVersionError(
        "Unable to compare this PDF version. Please make sure the current tax computation PDF can be generated, then try again.",
      );
    } finally {
      setComparingPdfVersionId(null);
    }
  };

  const handleOpenPdfComparison = () => {
    if (!pdfComparisonResult?.url) return;
    window.open(pdfComparisonResult.url, "_blank", "noopener,noreferrer");
  };

  const handleStartRenamePdfVersion = (version) => {
    setEditingVersionId(version.id);
    setEditingVersionName(version.fileName || "");
    setPdfVersionError("");
  };

  const handleSavePdfVersionName = async (version) => {
    const fileName = editingVersionName.trim();
    if (!fileName) {
      setPdfVersionError("File name is required.");
      return;
    }

    setPdfVersionActionId(version.id);
    setPdfVersionError("");

    try {
      await updatePdfVersionName(version.id, { fileName });
      setEditingVersionId(null);
      setEditingVersionName("");
      await loadPdfVersions();
      handleSuccess?.("PDF version name updated.");
    } catch (error) {
      setPdfVersionError(error?.message || "Failed to update PDF version.");
    } finally {
      setPdfVersionActionId(null);
    }
  };

  const handleDeletePdfVersion = async () => {
    if (!versionToDelete) return;

    setPdfVersionActionId(versionToDelete.id);
    setPdfVersionError("");

    try {
      await deletePdfVersion(versionToDelete.id);
      setVersionToDelete(null);
      await loadPdfVersions();
      handleSuccess?.("PDF version deleted.");
    } catch (error) {
      setPdfVersionError(error?.message || "Failed to delete PDF version.");
    } finally {
      setPdfVersionActionId(null);
    }
  };

  const handleDeleteAllPdfVersions = async () => {
    if (!currentTaxComputationId) {
      setPdfVersionError("Unable to identify current tax computation.");
      return;
    }

    setPdfVersionActionId("all");
    setPdfVersionError("");

    try {
      await deleteAllPdfVersions(currentTaxComputationId);
      setDeleteAllVersionsDialogOpen(false);
      setEditingVersionId(null);
      setEditingVersionName("");
      await loadPdfVersions();
      handleSuccess?.("All PDF versions deleted.");
    } catch (error) {
      setPdfVersionError(error?.message || "Failed to delete PDF versions.");
    } finally {
      setPdfVersionActionId(null);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Header company={company} otherDetails={states.details}>
        {canEditTaxComputation && company?.companyType !== "investment" && (
          <Button
            variant="outlined"
            onClick={() => setRolloverDialogOpen(true)}
            disabled={rolloverExecution.busy}
            startIcon={<RefreshIcon />}
          >
            Roll Over Data
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleOpenVersionDialog}
          startIcon={<FolderCopyIcon />}
        >
          Stored PDF
        </Button>
        <Button
          variant="contained"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          startIcon={
            isGenerating ? <CircularProgress size={20} /> : <PrintIcon />
          }
          endIcon={<ArrowDropDownIcon />}
          disabled={isGenerating}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            boxShadow: (theme) =>
              `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: (theme) =>
              `linear-gradient(45deg, ${
                theme.palette.primary.main
              } 30%, ${alpha(theme.palette.primary.main, 0.8)} 90%)`,
            "&:hover": {
              boxShadow: (theme) =>
                `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
              background: (theme) =>
                `linear-gradient(45deg, ${
                  theme.palette.primary.dark
                } 30%, ${alpha(theme.palette.primary.main, 0.9)} 90%)`,
            },
            "&:disabled": {
              background: (theme) => theme.palette.action.disabledBackground,
              color: (theme) => theme.palette.action.disabled,
            },
          }}
        >
          {isGenerating ? "Generating..." : "Print All Schedules"}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              mt: 1.5,
              borderRadius: 2,
              boxShadow: (theme) =>
                `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
              minWidth: 320,
              "& .MuiList-root": {
                py: 1,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              Download Options
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <InfoOutlinedIcon fontSize="small" />
              Choose your preferred download format
            </Typography>
          </Box>

          <DownloadMenuOptions
            isGenerating={isGenerating}
            onDownload={handleDownloadAllSchedules}
          />

          <Box
            sx={{
              px: 2,
              py: 1.5,
              mt: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "& .MuiSvgIcon-root": {
                  fontSize: 16,
                },
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
              Note: Only saved statements and schedules will be included in the
              download.
              <br />
              Please ensure your data is saved before proceeding.
            </Typography>
          </Box>
        </Menu>
      </Header>

      <PdfGenerationOptionsDialog
        open={storePromptOpen}
        busy={
          isGenerating ||
          isStoringPdfVersion ||
          isLoadingPdfOutputPreference ||
          isSavingPdfOutputPreference ||
          isProcessingPdfOutputContinue
        }
        pdfType={pendingPdfDownload?.forView ? "draft" : "final"}
        storePdf={pdfOutputOptions.storePdf}
        footer={pdfOutputOptions.footer}
        onClose={handleClosePdfOutputOptions}
        onContinue={handleContinuePdfOutput}
        onStorePdfChange={handlePdfStorePreferenceChange}
        onFooterChange={handlePdfFooterChange}
      />

      <Dialog
        open={
          pdfGenerationLoadingOpen &&
          (isProcessingPdfOutputContinue ||
            isGenerating ||
            isStoringPdfVersion ||
            isLoadingStoreVersionLimit)
        }
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        aria-label="PDF generation progress"
      >
        <DialogContent sx={{ p: 0 }}>
          <DialogLoading
            title={
              isStoringPdfVersion
                ? "Generating and storing your PDF..."
                : "Preparing your PDF..."
            }
            content={
              isStoringPdfVersion
                ? "Please wait while the PDF is generated and stored."
                : "Please wait while your preferences and PDF are prepared."
            }
          />
        </DialogContent>
      </Dialog>

      <BaseDialog
        open={storeVersionDialogOpen}
        onClose={() => {
          if (!isStoringPdfVersion && !isLoadingStoreVersionLimit) {
            setStoreVersionDialogOpen(false);
            setPendingPdfDownload(null);
          }
        }}
        title="Store Current Tax Computation PDF"
        subtitle="Store the full tax computation PDF for this company and tax computation."
        icon={<FolderCopyIcon fontSize="small" />}
        iconColor="success.main"
        maxWidth="sm"
        closeDisabled={isStoringPdfVersion || isLoadingStoreVersionLimit}
        actions={
          <>
            <Button
              onClick={() => {
                setStoreVersionDialogOpen(false);
                setPendingPdfDownload(null);
              }}
              disabled={isStoringPdfVersion || isLoadingStoreVersionLimit}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleStorePrintAllPdfVersion}
              disabled={
                isStoringPdfVersion ||
                isLoadingStoreVersionLimit ||
                storeVersionLimitInfo?.remainingStorageBytes === 0
              }
              startIcon={<FolderCopyIcon />}
            >
              {isStoringPdfVersion ? "Storing..." : "Store"}
            </Button>
          </>
        }
      >
        {pendingPdfDownload?.lockPdfType ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              PDF type
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {storeVersionPdfType === "draft" ? "Draft PDF" : "Final PDF"}
            </Typography>
          </Box>
        ) : (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={storeVersionPdfType}
            onChange={handleStoreVersionTypeChange}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="final">Final</ToggleButton>
            <ToggleButton value="draft">Draft</ToggleButton>
          </ToggleButtonGroup>
        )}
        <TextField
          fullWidth
          label="Name of file"
          value={storeVersionFileName}
          onChange={(event) => setStoreVersionFileName(event.target.value)}
          disabled={isStoringPdfVersion}
          margin="dense"
        />
        {storeVersionLimitInfo && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Available space: {getPdfStorageLabel(storeVersionLimitInfo)}
          </Typography>
        )}
        {storeVersionError && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1, overflowWrap: "anywhere" }}
          >
            {storeVersionError}
          </Typography>
        )}
      </BaseDialog>

      <BaseDialog
        open={versionDialogOpen}
        onClose={() => {
          if (!pdfVersionActionId && !comparingPdfVersionId)
            setVersionDialogOpen(false);
        }}
        title="Stored Tax Computation PDFs"
        subtitle={getPdfStorageLabel(pdfVersionMeta)}
        icon={<FolderCopyIcon fontSize="small" />}
        maxWidth="md"
        closeDisabled={Boolean(pdfVersionActionId || comparingPdfVersionId)}
        actions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<FolderCopyIcon />}
                onClick={() =>
                  openStoreVersionDialog({ forView: false, download: false })
                }
                disabled={
                  Boolean(pdfVersionActionId || comparingPdfVersionId) ||
                  !canEditTaxComputation
                }
              >
                Store Tax Computation PDF
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteAllVersionsDialogOpen(true)}
                disabled={
                  Boolean(pdfVersionActionId) ||
                  Boolean(comparingPdfVersionId) ||
                  !canEditTaxComputation ||
                  pdfVersions.length === 0
                }
                sx={{
                  bgcolor: "error.main",
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.9),
                  },
                }}
              >
                Delete All
              </Button>
            </Stack>
            <Button
              variant="outlined"
              onClick={() => setVersionDialogOpen(false)}
              disabled={Boolean(pdfVersionActionId || comparingPdfVersionId)}
            >
              Close
            </Button>
          </Box>
        }
      >
        <Box tabIndex={-1} sx={{ outline: "none" }} />
        {isLoadingPdfVersions ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : pdfVersions.length === 0 ? (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <FolderCopyIcon sx={{ fontSize: 40, mb: 1, opacity: 0.45 }} />
            <Typography variant="body2">No saved PDF versions yet.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {pdfVersions.map((version) => {
              const isEditing = editingVersionId === version.id;
              const isBusy = pdfVersionActionId === version.id;
              const isComparing = comparingPdfVersionId === version.id;
              const isRowBusy = isBusy || isComparing;

              return (
                <Box
                  key={version.id}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        label="Name of file"
                        value={editingVersionName}
                        onChange={(event) =>
                          setEditingVersionName(event.target.value)
                        }
                        disabled={isRowBusy}
                      />
                    ) : (
                      <Typography variant="subtitle2" noWrap fontWeight={700}>
                        {version.fileName}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {new Date(version.createdAt).toLocaleString()} ·{" "}
                      {version.pdfType === "draft" ? "Draft" : "Final"} ·{" "}
                      {formatPdfVersionSize(version.fileSize)}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent={{ xs: "flex-end", sm: "center" }}
                    sx={{ flexShrink: 0 }}
                  >
                    {isEditing ? (
                      <>
                        <Tooltip title="Save name">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleSavePdfVersionName(version)}
                              disabled={isRowBusy}
                            >
                              {isRowBusy ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SaveOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel rename">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingVersionId(null);
                                setEditingVersionName("");
                              }}
                              disabled={isRowBusy}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Download">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleDownloadPdfVersion(version)}
                              disabled={
                                isRowBusy || Boolean(comparingPdfVersionId)
                              }
                            >
                              {isBusy ? (
                                <CircularProgress size={18} />
                              ) : (
                                <FileDownloadOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Compare with current PDF">
                          <span>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => handleComparePdfVersion(version)}
                              disabled={
                                isRowBusy ||
                                Boolean(
                                  pdfVersionActionId || comparingPdfVersionId,
                                )
                              }
                            >
                              {isComparing ? (
                                <CircularProgress size={18} />
                              ) : (
                                <CompareIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit name">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleStartRenamePdfVersion(version)
                              }
                              disabled={!canEditTaxComputation || isRowBusy}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => setVersionToDelete(version)}
                              disabled={!canEditTaxComputation || isRowBusy}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        {pdfComparisonResult?.url && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Comparison is ready for {pdfComparisonResult.label}.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenPdfComparison}
            >
              Open Comparison
            </Button>
          </Box>
        )}

        {pdfVersionError && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 2, overflowWrap: "anywhere" }}
          >
            {pdfVersionError}
          </Typography>
        )}
      </BaseDialog>

      <ConfirmationDialog
        open={Boolean(versionToDelete)}
        title="Delete PDF Version"
        description={`Delete "${versionToDelete?.fileName || "this PDF version"}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        onConfirm={handleDeletePdfVersion}
        onClose={() => {
          if (!pdfVersionActionId) setVersionToDelete(null);
        }}
      />

      <ConfirmationDialog
        open={deleteAllVersionsDialogOpen}
        title="Delete All PDF Versions"
        description={`Delete all ${pdfVersions.length} stored PDF version${
          pdfVersions.length === 1 ? "" : "s"
        } for this tax computation? This cannot be undone.`}
        confirmLabel="Delete All"
        cancelLabel="Cancel"
        confirmColor="error"
        onConfirm={handleDeleteAllPdfVersions}
        onClose={() => {
          if (!pdfVersionActionId) setDeleteAllVersionsDialogOpen(false);
        }}
      />

      <Grid container spacing={3} sx={{ mt: "0px !important" }}>
        <Grid item xs={12} md={company?.companyType === "investment" ? 12 : 6}>
          <SchedulePanel
            panelKey="pl"
            title="P&L Schedules"
            icon={BlurLinearIcon}
            schedules={plSchedules}
            filteredSchedules={filteredPlSchedules}
            searchQuery={plSearchQuery}
            setSearchQuery={setPlSearchQuery}
            onAddSchedule={onAddSchedule}
            onOpenSchedule={onOpenSchedule}
            moveSchedule={moveSchedule}
            reorderSchedule={reorderSchedule}
            DndItemComponent={DndItem}
            canEditSchedule={canEditSchedule}
            isFetching={isFetching}
            emptyStateStyles={emptyStateStyles}
          />
        </Grid>

        {company?.companyType !== "investment" && (
          <Grid item xs={12} md={6}>
            <SchedulePanel
              panelKey="ca"
              title="CA Schedules"
              icon={AccountBalanceIcon}
              schedules={caSchedules}
              filteredSchedules={filteredCaSchedules}
              searchQuery={caSearchQuery}
              setSearchQuery={setCaSearchQuery}
              onAddSchedule={onAddCaSchedule}
              onOpenSchedule={onOpenCaSchedule}
              moveSchedule={moveCaSchedule}
              reorderSchedule={reorderCaSchedule}
              DndItemComponent={CADndItem}
              canEditSchedule={canEditSchedule}
              isFetching={isFetching}
              emptyStateStyles={emptyStateStyles}
            />
          </Grid>
        )}
      </Grid>

      {/* Rollover Dialog */}
      <RolloverDataDialog
        open={rolloverDialogOpen}
        onClose={() => {
          setRolloverDialogOpen(false);
          setPendingRolloverData(null);
        }}
        onConfirm={handleRolloverConfirm}
        loadingComputations={rolloverComputations.loading}
        computationsError={rolloverComputations.error}
        onRetryComputations={rolloverComputations.retry}
        previousYearComputations={rolloverComputations.computations}
        initialSourceId={pendingRolloverData?.sourceComputation?.tc_id}
        currentYearOfAssessment={yearOfAssessment}
        currentTaxComputationId={currentTaxComputationId}
      />

      {/* Rollover Confirmation Dialog */}
      <RolloverConfirmationDialog
        open={confirmRolloverDialogOpen && !rolloverExecution.busy}
        sourceYear={pendingRolloverData?.sourceComputation?.yearOfAssessment}
        targetYear={yearOfAssessment}
        onClose={() => {
          if (!rolloverExecution.busy) {
            setConfirmRolloverDialogOpen(false);
            setRolloverDialogOpen(true);
          }
        }}
        onBack={() => {
          setConfirmRolloverDialogOpen(false);
          setRolloverDialogOpen(true);
        }}
        onConfirm={executeRollover}
      />

      {/* Rollover execution status */}
      <RolloverProgressDialog
        status={rolloverExecution.status}
        error={rolloverExecution.error}
        sourceYear={pendingRolloverData?.sourceComputation?.yearOfAssessment}
        targetYear={yearOfAssessment}
        onClose={() => {
          rolloverExecution.reset();
          setPendingRolloverData(null);
        }}
      />

      {/* Post-Rollover Reminder Dialog */}
      <PostRolloverReminderDialog
        open={postRolloverDialogOpen}
        onClose={() => setPostRolloverDialogOpen(false)}
        hasHirePurchaseSchedule={
          caSchedules && caSchedules.some(
            (schedule) => schedule.scheduleType === "HirePurchase",
          )
        }
        onOpenHirePurchase={() => {
          const hirePurchaseSchedule = caSchedules.find(
            (schedule) => schedule.scheduleType === "HirePurchase",
          );
          if (hirePurchaseSchedule && onOpenCaSchedule) {
            setPostRolloverDialogOpen(false);
            onOpenCaSchedule(
              hirePurchaseSchedule,
              hirePurchaseSchedule.scheduleType,
            );
          }
        }}
      />
    </Box>
  );
};

export default ContentPage;
